/**
 * `{{variable}}` references: the one predicate the whole collections secrets
 * contract turns on, in one place so the write side and the (later) read side
 * cannot drift into disagreeing about what a "pointer" is.
 *
 * ## Why a template reference is safe and a literal is not
 *
 * A stored Apiboo request is a TEMPLATE by construction. Substitution happens
 * at send time, into fresh locals inside `sendHttpRequest`
 * (`components/RequestBuilder.js:1413-1417, 1524-1533`), and neither of the two
 * save payloads (`:1751` Ctrl+S, `:1770` Save) contains a resolved value — they
 * are the raw editor state. So `Authorization: Bearer {{apiToken}}` in a stored
 * record IS the whole record: there is no resolved copy of it anywhere on that
 * row, on any backend.
 *
 * The app already draws this exact line and says why, in
 * `utils/secretHeuristics.js:47-49`:
 *
 *     // A template reference is a pointer, not a secret — redacting it would
 *     // hide the wiring the model needs to explain the request.
 *
 * That is the READ-side use. This module exists because phase 5 needs the same
 * predicate on the WRITE side too (**O32**): a literal credential in an auth
 * block is REFUSED, and "is this a literal?" is exactly "is this not a
 * pointer?".
 *
 * ## Partial disclosure is disclosure
 *
 * `{{host}}-prod-9f8a7b` is NOT pointer-only. A value that is part reference
 * and part literal carries the literal, and there is no rule that makes half a
 * credential safe. The remainder after stripping every `{{...}}` and an
 * optional leading auth scheme word must be empty or punctuation — nothing
 * else counts.
 *
 * This is a STRUCTURAL predicate, not a heuristic, and that distinction is the
 * reason it is here rather than in `secretHeuristics.js`: `looksSecret` /
 * `redactSecretsInText` are best-effort by their own words
 * (`secretHeuristics.js:107-109`) and are the right tool where free text is
 * unavoidable. A field allowlist is available here, so a probabilistic test
 * would be strictly worse.
 */

/** `{{ name }}` — the same shape `utils/resolveVariables.js:27` resolves. */
const TEMPLATE_RE = /\{\{\s*[^}]+\s*\}\}/g;

/**
 * A leading HTTP auth scheme word. `Bearer {{t}}` must be accepted, because
 * the scheme is a protocol constant and not a secret — Apiboo's own auth
 * editor writes exactly that shape.
 */
const SCHEME_RE = /^\s*(bearer|basic|token|digest)\b/i;

/** Whatever may legally remain once the references and the scheme are gone. */
const PUNCTUATION_ONLY_RE = /^[\s:;,.\-_/=+&?()[\]{}'"]*$/;

/** True when `value` carries at least one `{{reference}}`. */
export function hasTemplateRef(value) {
  if (typeof value !== 'string') return false;
  TEMPLATE_RE.lastIndex = 0;
  return TEMPLATE_RE.test(value);
}

/**
 * True when `value` carries no literal content of its own: it is empty, or it
 * is made only of `{{references}}`, an optional leading scheme word, and
 * punctuation.
 *
 * `''` and `'   '` are pointer-only, deliberately — an EMPTY field is not a
 * credential, and refusing it would stop an agent from creating the `bearer`
 * slot for the user to fill in.
 */
export function isPointerOnly(value) {
  if (value == null) return true;
  if (typeof value !== 'string') return false;
  const withoutRefs = value.replace(TEMPLATE_RE, '');
  const withoutScheme = withoutRefs.replace(SCHEME_RE, '');
  return PUNCTUATION_ONLY_RE.test(withoutScheme);
}

/** Every distinct `{{name}}` in a string, in first-seen order. Names only. */
export function templateRefNames(value) {
  if (typeof value !== 'string') return [];
  const out = [];
  const re = /\{\{\s*([^}]+?)\s*\}\}/g;
  let m = re.exec(value);
  while (m) {
    if (!out.includes(m[1])) out.push(m[1]);
    m = re.exec(value);
  }
  return out;
}
